import { useMemo, useState, useEffect } from 'react'
import { api } from '../lib/api'
import { ItemValidationBadges } from './ItemValidationBadges'

const box = { background: '#161d31', borderRadius: 12, padding: 16, marginBottom: 16, color: '#f8fafc' }
const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }
const subtleBtn = { ...btn, background: '#334155' }
const ghostBtn = { ...btn, background: '#1e293b', color: '#cbd5e1' }
const input = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: 'white' }
const label = { display: 'block', fontSize: 14, color: '#94a3b8', marginBottom: 6 }

function renderChoiceRow(choice, index) {
  return (
    <div key={`${choice.label || index}-${index}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: index === 0 ? 0 : '1px solid #1e293b' }}>
      <div style={{ width: 28, color: choice.is_correct ? '#86efac' : '#94a3b8', fontWeight: 700 }}>{choice.label || String.fromCharCode(65 + index)}</div>
      <div style={{ flex: 1 }}>
        <div>{choice.choice_text || 'Empty choice'}</div>
        {choice.is_correct ? <div style={{ color: '#86efac', fontSize: 12, marginTop: 2 }}>Correct answer</div> : null}
      </div>
    </div>
  )
}

function classificationTone(status) {
  if (status === 'manual') return { bg: '#172554', fg: '#93c5fd' }
  if (status === 'accepted') return { bg: '#052e16', fg: '#86efac' }
  if (status === 'suggested') return { bg: '#3f2c06', fg: '#fcd34d' }
  return { bg: '#1e293b', fg: '#94a3b8' }
}

function classificationSummary(metadata, kind, referenceRows = []) {
  const item = metadata?.[kind] || {}
  const selectedId = item.selected_id ?? item.suggested_id ?? item.candidate_id ?? null
  const matched = selectedId != null ? referenceRows.find(row => Number(row.id) === Number(selectedId)) : null
  const label = item.selected_name || item.suggested_name || item.candidate_name || matched?.name || 'Unassigned'
  const status = item.status || 'unassigned'
  return {
    label,
    status,
    confidence: item.confidence ? `${Math.round(Number(item.confidence) * 100)}%` : '0%',
    source: item.source || 'none',
  }
}

export function ReviewDetailsPanel({
  item = null,
  categories = [],
  vtneDomains = [],
  onSave = () => {},
  onSaveAndNext = () => {},
  onApprove = () => {},
  onApproveAndNext = () => {},
  onReject = () => {},
  onRejectAndNext = () => {},
  onEscalate = () => {},
  onEscalateAndNext = () => {},
  onDuplicateSuggestion = () => {},
  saving = false,
  showCompare = false,
  compareWith = null,
  queuePosition = 0,
  queueCount = 0,
}) {
  const [form, setForm] = useState({})
  const [duplicateSuggestions, setDuplicateSuggestions] = useState([])
  const [compareData, setCompareData] = useState(null)

  const selectedCategoryName = useMemo(
    () => categories.find(c => Number(c.id) === Number(form.proposed_category_id))?.name || 'Unassigned',
    [categories, form.proposed_category_id],
  )

  const selectedDomainName = useMemo(
    () => vtneDomains.find(d => Number(d.id) === Number(form.proposed_vtne_domain_id))?.name || 'Unassigned',
    [vtneDomains, form.proposed_vtne_domain_id],
  )

  const selectedCategorySummary = useMemo(
    () => classificationSummary(item?.classification_metadata, 'category', categories),
    [item, categories],
  )

  const selectedDomainSummary = useMemo(
    () => classificationSummary(item?.classification_metadata, 'vtne_domain', vtneDomains),
    [item, vtneDomains],
  )

  useEffect(() => {
    if (item) {
      setForm({
        proposed_admin_title: item.proposed_admin_title || '',
        proposed_stem: item.proposed_stem || '',
        proposed_rationale: item.proposed_rationale || '',
        proposed_category_id: item.proposed_category_id || '',
        proposed_vtne_domain_id: item.proposed_vtne_domain_id || '',
        duplicate_of_question_id: item.duplicate_of_question_id || '',
      })
      loadDuplicates(item.id)
    }
  }, [item])

  async function loadDuplicates(itemId) {
    try {
      const dupes = await api.duplicateSuggestions(itemId)
      setDuplicateSuggestions(dupes)
    } catch (err) {
      console.error('Error loading duplicates:', err)
    }
  }

  async function handleCompare(questionId) {
    try {
      const data = await api.compareImportItem(item.id, questionId)
      setCompareData(data)
    } catch (err) {
      console.error('Error comparing:', err)
    }
  }

  if (!item) {
    return (
      <div style={box}>
        <h2 style={{ marginTop: 0, marginBottom: 16 }}>Review Inspector</h2>
        <div style={{ color: '#94a3b8' }}>Select an item from the queue to review it here.</div>
      </div>
    )
  }

  const isNeedsConversion = item.triage_bucket === 'needs_conversion'

  return (
    <div style={{ position: 'sticky', top: 16 }}>
      {/* Header with quick actions */}
      <div style={{ ...box, position: 'sticky', top: 16, zIndex: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>
              {queueCount > 0 ? `Review item ${queuePosition} of ${queueCount}` : 'Review item'}
            </div>
            <h2 style={{ marginTop: 0, marginBottom: 4 }}>{item.proposed_admin_title || 'Untitled'}</h2>
            <div style={{ color: '#94a3b8', fontSize: 13 }}>
              {item.triage_bucket.replace('_', ' ')} • {item.review_status}
            </div>
            <div style={{ marginTop: 8 }}>
              <ItemValidationBadges item={item} compact={true} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button style={btn} onClick={() => onApproveAndNext(item.id)}>Approve + Next</button>
            <button style={subtleBtn} onClick={() => onRejectAndNext(item.id)}>Reject + Next</button>
            <button style={ghostBtn} onClick={() => onEscalateAndNext(item.id)}>Escalate + Next</button>
          </div>
        </div>
      </div>

      {isNeedsConversion && (
        <div style={{ ...box, background: '#2e1065', color: '#f5e8ff', border: '1px solid #7e22ce' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Format conversion required</div>
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            This item is blocked from the canonical bank until it is converted into a 4-choice multiple-choice question.
            {item.triage_flags?.true_false ? ' It is currently a true/false item.' : ' It currently does not satisfy the required answer-choice format.'}
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Left: Content and metadata */}
        <div>
          {/* Stem */}
          <div style={box}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Question Stem</h3>
            <div style={{ lineHeight: 1.6, color: '#e2e8f0' }}>
              {item.proposed_stem || 'No stem found.'}
            </div>
          </div>

          {/* Choices */}
          <div style={box}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Choices</h3>
            <div style={{ background: '#0f172a', borderRadius: 8, padding: '0 12px' }}>
              {item.proposed_choices?.length ? item.proposed_choices.map(renderChoiceRow) : <div style={{ padding: '12px 0', color: '#94a3b8' }}>No choices parsed.</div>}
            </div>
          </div>

          {/* Metadata summary */}
          <div style={box}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Classification Status</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ background: '#0f172a', borderRadius: 8, padding: 12 }}>
                <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>Category</div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{selectedCategoryName}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ ...classificationTone(selectedCategorySummary.status), padding: '3px 8px', borderRadius: 999, fontSize: 11 }}>
                    {selectedCategorySummary.status}
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>{selectedCategorySummary.confidence}</span>
                </div>
              </div>
              <div style={{ background: '#0f172a', borderRadius: 8, padding: 12 }}>
                <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>VTNE Domain</div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{selectedDomainName}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ ...classificationTone(selectedDomainSummary.status), padding: '3px 8px', borderRadius: 999, fontSize: 11 }}>
                    {selectedDomainSummary.status}
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>{selectedDomainSummary.confidence}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Edit form */}
        <div>
          <div style={box}>
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>Edit & Classify</h3>

            <label style={label}>Admin Title</label>
            <input
              style={input}
              value={form.proposed_admin_title || ''}
              onChange={e => setForm({ ...form, proposed_admin_title: e.target.value })}
              placeholder="Descriptive title for this question"
            />

            <div style={{ height: 12 }} />
            <label style={label}>Category</label>
            <select
              style={input}
              value={form.proposed_category_id || ''}
              onChange={e => setForm({ ...form, proposed_category_id: e.target.value })}
            >
              <option value="">Choose category</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <div style={{ height: 12 }} />
            <label style={label}>VTNE Domain</label>
            <select
              style={input}
              value={form.proposed_vtne_domain_id || ''}
              onChange={e => setForm({ ...form, proposed_vtne_domain_id: e.target.value })}
            >
              <option value="">Choose domain</option>
              {vtneDomains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>

            <div style={{ height: 12 }} />
            <label style={label}>Rationale (why this is correct)</label>
            <textarea
              style={{ ...input, minHeight: 80 }}
              value={form.proposed_rationale || ''}
              onChange={e => setForm({ ...form, proposed_rationale: e.target.value })}
              placeholder="Explain why the answer is correct"
            />

            <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={btn} onClick={() => onSave(form)} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button style={subtleBtn} onClick={() => onSaveAndNext(form)} disabled={saving}>
                {saving ? 'Saving…' : 'Save + Next'}
              </button>
              <button style={ghostBtn} onClick={() => onApprove(item.id)}>
                Approve
              </button>
              <button style={ghostBtn} onClick={() => onReject(item.id)}>
                Reject
              </button>
            </div>
          </div>

          {/* Duplicate suggestions */}
          {duplicateSuggestions.length > 0 && (
            <div style={box}>
              <h3 style={{ marginTop: 0, marginBottom: 12 }}>Possible Duplicates</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {duplicateSuggestions.map(s => (
                  <div key={s.question_id} style={{ background: '#0f172a', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{s.admin_title}</div>
                    <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6 }}>Score: {s.score}</div>
                    <button
                      style={{ ...ghostBtn, fontSize: 12, padding: '6px 10px' }}
                      onClick={() => handleCompare(s.question_id)}
                    >
                      Compare
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compare view */}
      {compareData && (
        <div style={box}>
          <h3 style={{ marginTop: 0 }}>Comparison: Staged vs Canonical</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <h4 style={{ marginTop: 0 }}>Staged Item</h4>
              <div style={{ color: '#cbd5e1' }}>{item.proposed_stem}</div>
            </div>
            <div>
              <h4 style={{ marginTop: 0 }}>Canonical Question</h4>
              <div style={{ color: '#cbd5e1' }}>{compareData.canonical_stem}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
